import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  ClockIcon,
  EyeIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  LightBulbIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const InsightsPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Market Analysis', 'Strategy', 'Technology', 'Whitepapers'];

  const insights = [
    {
      id: 1,
      title: "The Evolution of Token Liquidity: Lessons from Traditional Markets",
      excerpt: "How institutional liquidity management principles are being adapted for the decentralized economy.",
      category: "Market Analysis",
      readTime: "8 min read",
      views: "2.4k",
      date: "Dec 15, 2024",
      type: "Article",
      image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3",
      featured: true
    },
    {
      id: 2,
      title: "Market Maker Selection Framework: A Quantitative Approach",
      excerpt: "Our proprietary methodology for evaluating and selecting optimal market making partners.",
      category: "Strategy",
      readTime: "12 min read",
      views: "1.8k",
      date: "Dec 10, 2024",
      type: "Whitepaper",
      image: "https://images.unsplash.com/photo-1560472355-536de3962603"
    },
    {
      id: 3,
      title: "DeFi vs CeFi: Optimizing Liquidity Across Venues",
      excerpt: "Comparative analysis of liquidity provision strategies across centralized and decentralized exchanges.",
      category: "Technology",
      readTime: "6 min read",
      views: "3.1k",
      date: "Dec 5, 2024",
      type: "Article",
      image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0"
    },
    {
      id: 4,
      title: "Tokenomics & Liquidity: The Hidden Correlation",
      excerpt: "How tokenomics design directly impacts liquidity performance and market efficiency.",
      category: "Strategy",
      readTime: "10 min read",
      views: "2.7k",
      date: "Nov 28, 2024",
      type: "Article",
      image: "https://images.unsplash.com/photo-1640340434855-6084b1f4901c"
    },
    {
      id: 5,
      title: "Real-Time Liquidity Monitoring: A Technical Deep Dive",
      excerpt: "The infrastructure and methodologies behind effective liquidity performance tracking.",
      category: "Technology",
      readTime: "15 min read",
      views: "1.2k",
      date: "Nov 20, 2024",
      type: "Whitepaper",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71"
    },
    {
      id: 6,
      title: "2024 Crypto Liquidity Landscape Report",
      excerpt: "Comprehensive analysis of market trends, key players, and emerging opportunities.",
      category: "Market Analysis",
      readTime: "25 min read",
      views: "4.6k",
      date: "Nov 15, 2024",
      type: "Report",
      image: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f"
    }
  ];

  const filteredInsights = selectedCategory === 'All' 
    ? insights 
    : insights.filter(insight => insight.category === selectedCategory);

  const featuredInsight = insights.find(insight => insight.featured);

  return (
    <div className="min-h-screen bg-primary-900 pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-primary-800 to-primary-900">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              ref={ref}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-block px-6 py-2 glass-card text-accent-gold text-sm font-medium tracking-wide uppercase mb-6">
                Insights & Thought Leadership
              </div>
              <h1 className="text-display font-serif font-bold text-cream-100 mb-6">
                Deep Insights for the{' '}
                <span className="text-gradient">Token Economy</span>
              </h1>
              <p className="text-body-lg text-cream-200 max-w-3xl mx-auto">
                Strategic commentary, market analysis, and thought leadership from our team of 
                liquidity experts and institutional finance veterans.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredInsight && (
        <section className="py-16 bg-primary-900">
          <div className="section-padding">
            <div className="container-width">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="glass-card p-8 lg:p-12 mb-16"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <div className="flex items-center space-x-4 mb-6">
                      <span className="px-3 py-1 bg-accent-gold/20 text-accent-gold text-xs font-medium rounded-full">
                        Featured
                      </span>
                      <span className="px-3 py-1 bg-accent-blue/20 text-accent-blue text-xs font-medium rounded-full">
                        {featuredInsight.type}
                      </span>
                    </div>
                    
                    <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-4">
                      {featuredInsight.title}
                    </h2>
                    
                    <p className="text-cream-200 mb-6 leading-relaxed">
                      {featuredInsight.excerpt}
                    </p>
                    
                    <div className="flex items-center space-x-6 mb-8 text-cream-300 text-sm">
                      <div className="flex items-center space-x-2">
                        <ClockIcon className="h-4 w-4" />
                        <span>{featuredInsight.readTime}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <EyeIcon className="h-4 w-4" />
                        <span>{featuredInsight.views} views</span>
                      </div>
                      <span>{featuredInsight.date}</span>
                    </div>
                    
                    <button className="btn-primary group">
                      Read Article
                      <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                  </div>
                  
                  <div className="relative">
                    <img 
                      src={featuredInsight.image}
                      alt={featuredInsight.title}
                      className="w-full h-80 object-cover rounded-2xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-primary-900/60 to-transparent rounded-2xl" />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="py-8 bg-primary-900">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-4 justify-center mb-8"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-accent-blue text-white shadow-lg'
                      : 'bg-white/10 text-cream-200 hover:bg-white/20'
                  }`}
                >
                  {category}
                </button>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-gradient-to-b from-primary-900 to-primary-800">
        <div className="section-padding">
          <div className="container-width">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredInsights.map((insight, index) => (
                <motion.article
                  key={insight.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="glass-card glass-card-hover group cursor-pointer overflow-hidden"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={insight.image}
                      alt={insight.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 to-transparent" />
                    
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                        insight.type === 'Article' ? 'bg-accent-blue/20 text-accent-blue' :
                        insight.type === 'Whitepaper' ? 'bg-accent-gold/20 text-accent-gold' :
                        'bg-purple-500/20 text-purple-400'
                      }`}>
                        {insight.type}
                      </span>
                    </div>
                    
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="text-accent-blue text-xs font-medium mb-1">
                        {insight.category}
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-heading-sm font-serif font-bold text-cream-100 mb-3 group-hover:text-white transition-colors duration-300 line-clamp-2">
                      {insight.title}
                    </h3>
                    
                    <p className="text-cream-300 text-sm mb-4 line-clamp-3">
                      {insight.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between text-cream-400 text-xs">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <ClockIcon className="h-3 w-3" />
                          <span>{insight.readTime}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <EyeIcon className="h-3 w-3" />
                          <span>{insight.views}</span>
                        </div>
                      </div>
                      <span>{insight.date}</span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>

            {/* Load More */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-center mt-16"
            >
              <button className="btn-secondary">
                Load More Articles
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16 bg-primary-800">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8 }}
              className="glass-card p-12 text-center max-w-4xl mx-auto"
            >
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-accent-blue to-accent-gold rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <LightBulbIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              
              <h3 className="text-heading-lg font-serif font-bold text-cream-100 mb-4">
                Stay Ahead of the Market
              </h3>
              
              <p className="text-cream-200 mb-8 max-w-2xl mx-auto">
                Get exclusive insights, market updates, and strategic commentary 
                delivered directly to your inbox. Join 500+ industry professionals.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-cream-100 placeholder-cream-400 focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                />
                <button className="btn-primary whitespace-nowrap">
                  Subscribe
                </button>
              </div>
              
              <p className="text-cream-400 text-xs mt-4">
                No spam. Unsubscribe at any time.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;